// @ts-nocheck
import { DateTime, Interval } from 'luxon';
import { get, cloneDeep } from 'lodash';
import intersectionBy from 'lodash/intersectionBy';
import { UpsellConfigOption } from '@greenkub-fronts/shared/interface';

export const createObjectURL = (data) => {
  return URL.createObjectURL(data);
};

export const toOptionsByKey = (
  data: any[],
  key: string,
): UpsellConfigOption[] => {
  return data.map((x) => ({
    label: x.name,
    value: x[key].toString(),
    key: x['id'].toString(),
    unitPrice: x.unitPrice,
    unitPriceWithVat: x.unitPriceWithVat,
    original: {
      display: get(x, 'display', undefined),
      deletedAt: x.deletedAt,
    },
    defaultCode: x.defaultCode ?? null,
  } as UpsellConfigOption));
};

export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const asyncFilter = async (arr, predicate) => {
  const results = await Promise.all(arr.map(predicate));
  return arr.filter((_v, index) => results[index]);
};

export const uniq = (a) => {
  const seen = {};
  return a.filter(function(item) {
    return seen.hasOwnProperty(item) ? false : (seen[item] = true);
  });
};

export const formatSymfonyValidationErrors = (errors) => {
  return Object.entries(errors.children).
    filter(([key, value]) => 'errors' in value).
    map(([key, value]) => [key, { message: value.errors[0] }]);
};

export const getCSSHeight = (element, withoutPadding = false) => {
  const rect = element.getBoundingClientRect();

  if (withoutPadding) {
    const computedStyle = getComputedStyle(element);
    return (
      rect.bottom -
      rect.top -
      parseInt(computedStyle.getPropertyValue('padding-top')) -
      parseInt(computedStyle.getPropertyValue('padding-bottom'))
    );
  }

  return rect.bottom - rect.top;
};

export const getCSSWidth = (element, withoutPadding = false) => {
  const rect = element.getBoundingClientRect();

  if (withoutPadding) {
    const computedStyle = getComputedStyle(element);
    return (
      rect.right -
      rect.left -
      parseInt(computedStyle.getPropertyValue('padding-left')) -
      parseInt(computedStyle.getPropertyValue('padding-right'))
    );
  }

  return rect.right - rect.left;
};

/**
 * @param {number} x
 * @param {boolean} showSign
 * @returns {string | number}
 */
export const formatCurrency = (x, showSign = false) => {
  if (x == null) {
    return 0;
  }
  const sign = showSign && x > 0 ? '+' : '';

  return (
    sign +
    x.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') +
    ' €'
  );
};

export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getRandomColor = () => {
  let colors = [
    '#c2185b',
    '#7b1fa2',
    '#512da8',
    '#303f9f',
    '#1976d2',
    '#00838f',
    '#00796b',
    '#2e7d32',
  ];
  let randKey = Math.floor(Math.random() * 8);
  return colors[randKey];
};

export const countWorkDaysInInterval = (start, end) => {
  let startDate = DateTime.fromISO(start);
  let endDate = DateTime.fromISO(end);
  let numberOfDays = 0;

  for (let d of Interval.fromDateTimes(startDate, endDate).splitBy({
    days: 1,
  })) {
    let weekDay = d.start.toFormat('cccc');
    let day = d.start.toFormat('dLLLL');
    if (weekDay !== 'Sunday' && day !== '1May') {
      numberOfDays++;
    }
  }

  return numberOfDays;
};

// export const isMailStock = (mail) => {
//   let emails = [
//     // 'david.lebee@greenkub.fr',
//     // 'arthur.lambert@greenkub.fr',
//     // 'baptiste.remblier@greenkub.fr',
//     // 'paulo@greenkub.fr',
//     // 'bruno.sultan@greenkub.fr',
//     // 'alex@keole.net',
//   ]

//   return emails.includes(mail)
// }

export const paginateItems = (items: any) => {
  const maxPerPage = 30;
  const maxLastPage = 15;

  const itemPages = [[]];

  let count = 0;
  let currentPage = 0;
  for (const item of items) {
    count += 1;
    if (item.description) count += 1;
    if (item.description2) count += 1;
    if (item.description3) count += 1;

    if (count / maxPerPage > currentPage + 1) {
      currentPage += 1;
      itemPages[currentPage] = [];
    }

    itemPages[currentPage].push(item);
  }

  const lastPage = [...itemPages[itemPages.length - 1]];

  let i = 0;
  count = 0;

  while (i < lastPage.length && count < maxLastPage) {
    const item = lastPage[i];

    count += 1;
    if (item.description) count += 1;
    if (item.description2) count += 1;
    if (item.description3) count += 1;

    i++;
  }

  if (count >= maxLastPage) {
    itemPages[itemPages.length] = [];
  }

  return itemPages;
};

/**
 * Transform les odooIds en ids correspondant aux entitées
 *
 * @param {any} entities
 * @param {string | string[]} odooIds
 * @return {string | string[]}
 */
export const transformOdooIdsToEntityIds = (entities, odooIds) => {
  if (!odooIds) {
    return undefined;
  }

  const odooIdsCopy = !Array.isArray(odooIds) ? [odooIds] : odooIds; // conversion en tableau
  const ids = odooIdsCopy.map((x) => ({ odooId: Number(x) })); // conversion en Integer
  const items = intersectionBy(entities, ids, 'odooId');

  if (!items.length) {
    return undefined;
  }

  if (!Array.isArray(odooIds)) {
    return items[0].id.toString();
  }

  return items.map((x) => x.id.toString());
};

/**
 * Remplace un ou des objets dans un tableau par d'autre en fonction d'une clé
 *
 * @param {*} data
 * @param {*} objs
 * @param {string} key
 */
export const replaceByKey = (data, objs, key, keepObj = false) => {
  if (!objs) {
    return data;
  }

  const dataCopy = cloneDeep(data);
  const objsCopy = cloneDeep(!Array.isArray(objs) ? [objs] : objs);

  for (let i = 0; i < dataCopy.length; i++) {
    const index = objsCopy.findIndex(
      (x) => get(x, key) === get(dataCopy[i], key),
    );
    if (index !== -1) {
      if (keepObj) {
        dataCopy[i] = { ...objsCopy[index], originalObj: dataCopy[i] };
      } else {
        dataCopy[i] = objsCopy[index];
      }
      objsCopy.splice(index, 1);
    }
  }

  return [...dataCopy, ...objsCopy];
};
